import type { FeatureFlagService } from "../../services/feature-flags";
import {
  createSpeechSchedule,
  ordersSpeechSegments,
  type BrowserSpeechOutService,
} from "../../services/speech";
import type { VersionedStorageService } from "../../services/versioned-storage";
import {
  DeliveryController,
  cueAtNode,
  deliveryActionForKey,
  deliveryDisplayNodes,
  deliveryDisplayStates,
  pensDownAfter,
  loadNotepad,
  saveNotepad,
  type DeliveryAction,
  type DeliverySpeaker,
  type DeliveryState,
} from "./delivery";
import { normalizeOrdersWhenEnabled } from "./normalization-access";
import type { PreflightSession } from "./preflight";
import {
  renderTraceSelection,
  type TraceAssetSelection,
  type TracePaneHandle,
} from "./trace";

export interface DeliverySpeechSetup {
  readonly baseMultiplier: number;
  readonly speech: BrowserSpeechOutService;
  readonly targetWpm: number;
  readonly voiceId: string;
  readonly voiceLabel: string;
}

export interface OrdersDeliveryOptions {
  readonly fills: Readonly<Record<string, string>>;
  readonly flags: FeatureFlagService;
  readonly session: PreflightSession;
  readonly speechSetup: DeliverySpeechSetup;
  readonly storage: VersionedStorageService;
  readonly traceSelection: TraceAssetSelection;
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function button(label: string, role: string): HTMLButtonElement {
  const control = element("button", label);
  control.type = "button";
  control.dataset.role = role;
  return control;
}

function speechSpeaker(setup: DeliverySpeechSetup): DeliverySpeaker {
  return {
    cancel: () => {
      setup.speech.cancel();
    },
    pause: () => {
      setup.speech.pause();
    },
    resume: () => {
      setup.speech.resume();
    },
    speak: async (request) => {
      const operation = setup.speech.speak({
        ...request,
        voiceId: setup.voiceId,
      });
      if (!(operation instanceof Promise)) {
        throw new Error("Speech output is unavailable in this build.");
      }
      await operation;
    },
  };
}

export function mountOrdersDelivery(
  target: HTMLElement,
  options: OrdersDeliveryOptions,
): () => void {
  if (!options.flags.isEnabled("delivery")) {
    target.replaceChildren(
      element("p", "Orders delivery is disabled in this build."),
    );
    return () => undefined;
  }

  const normalized = normalizeOrdersWhenEnabled(
    options.session.ordersTree,
    options.flags,
    { fills: options.fills },
  );
  if (!normalized.ok) {
    target.replaceChildren(element("p", normalized.error.message));
    return () => undefined;
  }

  const schedule = createSpeechSchedule(
    ordersSpeechSegments(normalized.value),
    { baseMultiplier: options.speechSetup.baseMultiplier },
    Math.random,
  );
  const nodes = deliveryDisplayNodes(options.session.ordersTree);

  const frame = element("section");
  frame.className = "delivery-frame";
  frame.dataset.role = "orders-delivery";

  const header = element("header");
  header.className = "delivery-header";
  const title = element(
    "h2",
    options.session.ordersTree.title ?? "Orders delivery",
  );
  const position = element("p", "Ready");
  position.dataset.role = "delivery-position";
  header.append(title, position);

  // R1 Step 13. Doc 04 section 1.2 gives EYES_UP a banner and a single tone;
  // the resume is a silent visual transition with no tone and no spoken call.
  const cueBanner = element("p", "Pens down, eyes up.");
  cueBanner.className = "cue-banner";
  cueBanner.dataset.role = "cue-banner";
  cueBanner.hidden = true;
  const cueBindings = options.session.cues.map(({ name, nodeId }) => ({
    name,
    nodeId,
  }));

  const amplitude = element("div");
  amplitude.className = "amplitude-bar";
  amplitude.dataset.role = "amplitude";
  amplitude.setAttribute("aria-hidden", "true");

  const workspace = element("div");
  workspace.className = "delivery-workspace";
  const ordersPane = element("section");
  ordersPane.className = "orders-pane";
  ordersPane.setAttribute("aria-label", "Orders text");
  const textElements = new Map<string, HTMLElement>();
  for (const node of nodes) {
    const tag =
      node.kind === "paragraph-heading"
        ? "h3"
        : node.kind === "subparagraph-heading"
          ? "h4"
          : "p";
    const line = element(tag, node.text);
    line.dataset.nodeId = node.id;
    line.className = `delivery-text ${node.kind}`;
    textElements.set(node.id, line);
    ordersPane.append(line);
  }
  const tracePane = element("aside");
  tracePane.className = "trace-pane";
  const traceContent = element("div");
  traceContent.dataset.role = "delivery-trace";
  tracePane.append(element("h3", "Trace"), traceContent);
  const trace: TracePaneHandle = renderTraceSelection(
    traceContent,
    options.traceSelection,
    options.flags,
    options.speechSetup.targetWpm,
  );
  workspace.append(ordersPane, tracePane);

  const notepadDrawer = element("details");
  notepadDrawer.className = "notepad-drawer";
  notepadDrawer.open = true;
  notepadDrawer.append(element("summary", "Notepad"));
  const notepad = element("textarea");
  notepad.dataset.role = "notepad";
  notepad.setAttribute("aria-label", "Notepad");
  const notepadStatus = element("p");
  notepadStatus.className = "storage-status";
  const loadedNotepad = loadNotepad(options.storage, options.session.fileHash);
  notepad.value = loadedNotepad.text;
  notepadStatus.textContent = loadedNotepad.issue ?? "Saved on this device.";
  notepad.addEventListener("input", () => {
    const issue = saveNotepad(
      options.storage,
      options.session.fileHash,
      notepad.value,
    );
    notepadStatus.textContent = issue ?? "Saved on this device.";
    notepadStatus.dataset.status = issue === null ? "saved" : "error";
  });
  notepadDrawer.append(notepad, notepadStatus);

  const transport = element("footer");
  transport.className = "delivery-transport";
  const previous = button("Previous", "delivery-previous");
  const playPause = button("Play", "delivery-play-pause");
  const stop = button("Stop", "delivery-stop");
  const next = button("Next", "delivery-next");
  const sayAgain = button("Say again", "delivery-say-again");
  const slower = button("-5 wpm", "delivery-slower");
  const faster = button("+5 wpm", "delivery-faster");
  const rate = element(
    "output",
    `${String(options.speechSetup.targetWpm)} wpm`,
  );
  rate.dataset.role = "delivery-rate";
  const voice = element("span", `Voice: ${options.speechSetup.voiceLabel}`);
  voice.className = "delivery-voice";
  const jumps = element("div");
  jumps.className = "paragraph-jumps";
  jumps.setAttribute("aria-label", "Jump to paragraph");
  for (let paragraph = 1; paragraph <= 5; paragraph += 1) {
    const jump = button(
      String(paragraph),
      `delivery-jump-${String(paragraph)}`,
    );
    jump.title = `Jump to paragraph ${String(paragraph)}`;
    jump.addEventListener("click", () => {
      controller.seek({ type: "JUMP", paragraph });
    });
    jumps.append(jump);
  }
  transport.append(
    previous,
    playPause,
    stop,
    next,
    sayAgain,
    slower,
    rate,
    faster,
    jumps,
    voice,
  );

  const error = element("p");
  error.className = "delivery-error";
  error.dataset.role = "delivery-error";
  error.hidden = true;

  frame.append(
    header,
    cueBanner,
    amplitude,
    workspace,
    notepadDrawer,
    transport,
    error,
  );
  target.replaceChildren(frame);

  let currentNodeId: string | null = null;
  let pensDown = false;
  const playCueTone = (): void => {
    try {
      const audio = new AudioContext();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.05;
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.18);
      oscillator.addEventListener("ended", () => void audio.close());
    } catch (failure: unknown) {
      // Never silent. Brave farbles Web Audio, so the tone can fail while the
      // banner is correct; say so rather than swallowing it.
      error.hidden = false;
      error.textContent =
        `The eyes-up tone could not play, but the banner is shown. ${failure instanceof Error ? failure.message : ""}`.trim();
    }
  };
  const renderState = (state: DeliveryState): void => {
    amplitude.dataset.status = state.status;
    playPause.textContent = state.status === "playing" ? "Pause" : "Play";
    rate.value = `${String(state.targetWpm)} wpm`;
    rate.textContent = rate.value;
    const states = deliveryDisplayStates(nodes, schedule, state);
    for (const display of states) {
      const line = textElements.get(display.id);
      if (line !== undefined) line.dataset.state = display.state;
    }
    const current = states.find(
      ({ state: textState }) => textState === "current",
    );
    position.textContent =
      state.status === "complete"
        ? "Complete"
        : (current?.text ?? state.status.toUpperCase());
    if (current !== undefined && current.id !== currentNodeId) {
      currentNodeId = current.id;
      // The one call. The trace pane decides whether that means anything:
      // with its feature off it holds an inert handle and this is a no-op.
      trace.deliveryReached(current.id);
      const cue = cueAtNode(cueBindings, current.id);
      const nextPensDown = pensDownAfter(pensDown, cue);
      if (nextPensDown !== pensDown) {
        pensDown = nextPensDown;
        cueBanner.hidden = !pensDown;
        frame.dataset.pensDown = String(pensDown);
        // EYES_UP carries a single tone. The resume is silent by Q16.
        if (cue === "EYES_UP") playCueTone();
      }
      textElements.get(current.id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const controller = new DeliveryController({
    schedule,
    targetWpm: options.speechSetup.targetWpm,
    speaker: speechSpeaker(options.speechSetup),
    clock: {
      wait: async (delayMs) =>
        await new Promise<void>((resolve) =>
          window.setTimeout(resolve, delayMs),
        ),
    },
    marker: () => Promise.resolve(),
    onError: (failure) => {
      error.hidden = false;
      error.textContent =
        failure instanceof Error ? failure.message : "Speech delivery failed.";
    },
    onState: renderState,
  });

  const seek = (
    action: Extract<DeliveryAction, { readonly type: "NEXT" | "PREVIOUS" }>,
  ): void => controller.seek(action);
  playPause.addEventListener("click", () => {
    if (controller.state().status === "playing") controller.pause();
    else controller.play();
  });
  stop.addEventListener("click", () => controller.stop());
  previous.addEventListener("click", () => seek({ type: "PREVIOUS" }));
  next.addEventListener("click", () => seek({ type: "NEXT" }));
  sayAgain.addEventListener("click", () => void controller.sayAgain());
  slower.addEventListener("click", () => controller.nudge(-5));
  faster.addEventListener("click", () => controller.nudge(5));

  const keydown = (event: KeyboardEvent): void => {
    const eventTarget = event.target;
    const isTyping =
      eventTarget instanceof HTMLInputElement ||
      eventTarget instanceof HTMLTextAreaElement ||
      eventTarget instanceof HTMLSelectElement ||
      (eventTarget instanceof HTMLElement && eventTarget.isContentEditable);
    if (isTyping && event.key !== "Escape") return;
    const action = deliveryActionForKey(event.key);
    if (action === null) return;
    event.preventDefault();
    if (action === "SAY_AGAIN") {
      void controller.sayAgain();
    } else if (action.type === "PLAY") {
      if (controller.state().status === "playing") controller.pause();
      else controller.play();
    } else if (action.type === "STOP") {
      controller.stop();
    } else if (action.type === "NUDGE_RATE") {
      controller.nudge(action.delta);
    } else {
      controller.seek(action);
    }
  };
  document.addEventListener("keydown", keydown);

  return () => {
    document.removeEventListener("keydown", keydown);
    controller.stop();
  };
}
